import { CSSProperties, FC, useEffect, useRef, useState } from 'react'

// CONTAINER PINBALL — TOS easter egg, the pinball variant.
// You're in the gantry crane operator's chair watching a container roll down
// the tilted yard. Two reach stackers at the bottom keep it in play. Hit
// container-stack bumpers and inspection gates to rack up TEU. Don't let it
// fall in the harbor.

const W = 480
const H = 720

const GRAVITY = 0.22
const BALL_RADIUS = 9
const FRICTION = 0.998
const RESTITUTION_WALL = 0.65
const RESTITUTION_BUMPER = 1.25
const SUB_STEPS = 4
const STUCK_SPEED = 0.6
const STUCK_THRESHOLD_FRAMES = 120

const FLIPPER_LEN = 60
const FLIPPER_W = 11
const FLIPPER_REST_ANGLE = 0.45
const FLIPPER_ACTIVE_ANGLE = -0.55
const FLIPPER_SWING_SPEED = 0.5
const FLIPPER_RETURN_SPEED = 0.2
const FLIPPER_PIVOT_Y = H - 100
const LEFT_FLIPPER_PIVOT_X = W / 2 - 85
const RIGHT_FLIPPER_PIVOT_X = W / 2 + 85

const PLUNGER_X = W - 22
const PLUNGER_REST_Y = H - 50
const PLUNGER_MAX_PULL = 50
const PLUNGER_LAUNCH_POWER = 0.42

const SCORE_BUMPER = 100
const SCORE_BUMPER_SUPER = 1000
const SCORE_SLINGSHOT = 50
const SCORE_GATE = 250
const SCORE_GATE_ALL = 5000
const SCORE_SPINNER = 25
const SCORE_LANE = 200
const SCORE_LANE_ALL = 3000
const BUMPER_SUPER_THRESHOLD = 5

const COMPANY_NAME = 'TERMINAL OS'

type WindowWithWebkitAudio = Window & { webkitAudioContext?: typeof AudioContext }

interface Segment {
  x1: number
  y1: number
  x2: number
  y2: number
}

interface Bumper {
  x: number
  y: number
  r: number
  hits: number
  super: boolean
  flashFrames: number
}

interface Slingshot {
  x1: number
  y1: number
  x2: number
  y2: number
  flashFrames: number
}

interface Gate {
  x: number
  y: number
  w: number
  h: number
  down: boolean
  flashFrames: number
}

interface Lane {
  x: number
  y: number
  lit: boolean
}

interface Spinner {
  x: number
  y: number
  angle: number
  angularVel: number
}

interface Ball {
  x: number
  y: number
  vx: number
  vy: number
  inPlay: boolean
  drainFrames: number
  stuckFrames: number
}

interface Plunger {
  y: number
  pulling: boolean
  power: number
}

interface Flipper {
  angle: number
  target: number
  prevAngle: number
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
  size: number
}

interface Floater {
  x: number
  y: number
  text: string
  color: string
  life: number
}

type GameStatus = 'ready' | 'playing' | 'gameover'

interface GameState {
  segments: Segment[]
  bumpers: Bumper[]
  slingshots: Slingshot[]
  gates: Gate[]
  lanes: Lane[]
  spinners: Spinner[]
  laneClosure: Segment
  plungerLaneClosed: boolean
  ball: Ball
  plunger: Plunger
  flippers: { left: Flipper; right: Flipper }
  score: number
  hi: number
  balls: number
  status: GameStatus
  time: number
  floaters: Floater[]
  particles: Particle[]
  shake: number
  multiplier: number
  multiplierFrames: number
  manifestClears: number
}

interface HudState {
  score: number
  hi: number
  balls: number
  status: GameStatus
  muted: boolean
  gatesLeft: number
}

interface ContainerPinballProps {
  onClose?: () => void
}

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

class GameAudio {
  ctx: AudioContext | null = null
  masterGain: GainNode | null = null
  muted = false

  init(): void {
    if (this.ctx) return
    try {
      const w = window as WindowWithWebkitAudio
      const Ctor = window.AudioContext || w.webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = 0.22
      this.masterGain.connect(this.ctx.destination)
    } catch {
      // Audio is non-essential; ignore unavailable AudioContext.
    }
  }

  private envelope(node: GainNode, attack: number, decay: number, peak = 1): void {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    node.gain.setValueAtTime(0, t)
    node.gain.linearRampToValueAtTime(peak, t + attack)
    node.gain.exponentialRampToValueAtTime(0.001, t + attack + decay)
  }

  bumper(superHit: boolean): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(superHit ? 880 : 660, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(superHit ? 220 : 165, this.ctx.currentTime + 0.15)
    this.envelope(gain, 0.001, 0.18, superHit ? 0.3 : 0.22)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.2)
  }

  flipper(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.value = 220
    this.envelope(gain, 0.001, 0.04, 0.1)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.05)
  }

  gate(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const ctxLocal = this.ctx
    const masterLocal = this.masterGain
    ;[330, 440, 660].forEach((freq, i) => {
      const osc = ctxLocal.createOscillator()
      const gain = ctxLocal.createGain()
      osc.type = 'square'
      osc.frequency.value = freq
      const t = ctxLocal.currentTime + i * 0.04
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.18, t + 0.005)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1)
      osc.connect(gain).connect(masterLocal)
      osc.start(t)
      osc.stop(t + 0.12)
    })
  }

  manifestCleared(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const ctxLocal = this.ctx
    const masterLocal = this.masterGain
    ;[523, 659, 784, 1047, 1318].forEach((freq, i) => {
      const osc = ctxLocal.createOscillator()
      const gain = ctxLocal.createGain()
      osc.type = 'triangle'
      osc.frequency.value = freq
      const t = ctxLocal.currentTime + i * 0.08
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.2, t + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25)
      osc.connect(gain).connect(masterLocal)
      osc.start(t)
      osc.stop(t + 0.27)
    })
  }

  spinner(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 1200
    this.envelope(gain, 0.001, 0.03, 0.1)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.04)
  }

  drain(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(440, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(80, this.ctx.currentTime + 0.6)
    this.envelope(gain, 0.005, 0.6, 0.25)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.65)
    const dur = 0.4
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    }
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'highpass'
    filter.frequency.value = 800
    const g2 = this.ctx.createGain()
    this.envelope(g2, 0.001, dur, 0.18)
    src.connect(filter).connect(g2).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)
  }

  launch(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(110, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(440, this.ctx.currentTime + 0.2)
    this.envelope(gain, 0.005, 0.22, 0.18)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.24)
  }

  toggleMute(): void {
    this.muted = !this.muted
  }
}

function buildTable(): {
  segments: Segment[]
  bumpers: Bumper[]
  slingshots: Slingshot[]
  gates: Gate[]
  lanes: Lane[]
  spinners: Spinner[]
  laneClosure: Segment
} {
  const segments: Segment[] = []

  const PLANE_W = 28
  const PLANE_X_INNER = W - PLANE_W - 8
  const PLANE_X_OUTER = W - 8
  const TOP_Y = 30

  const ARC_CX = W / 2
  const ARC_R = W / 2 - 8
  const realArcCY = TOP_Y + ARC_R

  const arcSteps = 24
  const arcA1 = Math.PI
  const arcA2 = 2 * Math.PI
  for (let i = 0; i < arcSteps; i++) {
    const a1 = arcA1 + (arcA2 - arcA1) * (i / arcSteps)
    const a2 = arcA1 + (arcA2 - arcA1) * ((i + 1) / arcSteps)
    segments.push({
      x1: ARC_CX + Math.cos(a1) * ARC_R,
      y1: realArcCY + Math.sin(a1) * ARC_R,
      x2: ARC_CX + Math.cos(a2) * ARC_R,
      y2: realArcCY + Math.sin(a2) * ARC_R,
    })
  }

  const ARC_BOTTOM_Y = realArcCY

  segments.push({ x1: 8, y1: ARC_BOTTOM_Y, x2: 8, y2: H - 180 })
  segments.push({
    x1: 8,
    y1: H - 180,
    x2: LEFT_FLIPPER_PIVOT_X - 6,
    y2: FLIPPER_PIVOT_Y + 2,
  })
  segments.push({
    x1: PLANE_X_INNER,
    y1: H - 180,
    x2: RIGHT_FLIPPER_PIVOT_X + 6,
    y2: FLIPPER_PIVOT_Y + 2,
  })
  segments.push({ x1: PLANE_X_OUTER, y1: ARC_BOTTOM_Y, x2: PLANE_X_OUTER, y2: H - 8 })
  segments.push({ x1: PLANE_X_INNER, y1: H - 8, x2: PLANE_X_OUTER, y2: H - 8 })

  const INNER_OPENING_Y = H / 2
  segments.push({ x1: PLANE_X_INNER, y1: H - 180, x2: PLANE_X_INNER, y2: INNER_OPENING_Y })

  const bumpers: Bumper[] = [
    { x: W / 2, y: 230, r: 24, hits: 0, super: false, flashFrames: 0 },
    { x: W / 2 - 70, y: 290, r: 22, hits: 0, super: false, flashFrames: 0 },
    { x: W / 2 + 70, y: 290, r: 22, hits: 0, super: false, flashFrames: 0 },
  ]

  const slingshots: Slingshot[] = []

  const gates: Gate[] = []
  for (let i = 0; i < 3; i++) {
    gates.push({
      x: 50,
      y: 380 + i * 36,
      w: 28,
      h: 22,
      down: false,
      flashFrames: 0,
    })
  }
  for (let i = 0; i < 3; i++) {
    gates.push({
      x: PLANE_X_INNER - 30,
      y: 380 + i * 36,
      w: 28,
      h: 22,
      down: false,
      flashFrames: 0,
    })
  }

  const lanes: Lane[] = [
    { x: W / 2 - 70, y: 130, lit: false },
    { x: W / 2, y: 110, lit: false },
    { x: W / 2 + 70, y: 130, lit: false },
  ]

  const spinners: Spinner[] = [{ x: W / 2, y: 360, angle: 0, angularVel: 0 }]

  // One-way gate that closes the lane opening once the ball has cleared into
  // the playfield. Spans from the existing inner-wall top up to the point
  // where the arc crosses the inner-wall x-coordinate.
  const arcInnerCrossY =
    realArcCY - Math.sqrt(ARC_R * ARC_R - (PLANE_X_INNER - ARC_CX) * (PLANE_X_INNER - ARC_CX))
  const laneClosure: Segment = {
    x1: PLANE_X_INNER,
    y1: INNER_OPENING_Y,
    x2: PLANE_X_INNER,
    y2: arcInnerCrossY,
  }

  return { segments, bumpers, slingshots, gates, lanes, spinners, laneClosure }
}

interface ClosestPoint {
  x: number
  y: number
  t: number
}

function closestPointOnSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): ClosestPoint {
  const dx = x2 - x1
  const dy = y2 - y1
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return { x: x1, y: y1, t: 0 }
  let t = ((px - x1) * dx + (py - y1) * dy) / len2
  t = clamp(t, 0, 1)
  return { x: x1 + t * dx, y: y1 + t * dy, t }
}

const ContainerPinball: FC<ContainerPinballProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<GameState | null>(null)
  const keysRef = useRef<Record<string, boolean>>({})
  const audioRef = useRef<GameAudio>(new GameAudio())
  const startGameRef = useRef<(() => void) | null>(null)
  const [hud, setHud] = useState<HudState>({
    score: 0,
    hi: 0,
    balls: 3,
    status: 'ready',
    muted: false,
    gatesLeft: 6,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const audio = audioRef.current

    const dpr = window.devicePixelRatio || 1
    const resize = (): void => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    let savedHi = 0
    try {
      savedHi = parseInt(localStorage.getItem('containerPinballHi') || '0', 10) || 0
    } catch {
      // localStorage may be unavailable; ignore.
    }

    const initState = (): GameState => {
      const table = buildTable()
      return {
        ...table,
        ball: {
          x: PLUNGER_X,
          y: PLUNGER_REST_Y - BALL_RADIUS - 1,
          vx: 0,
          vy: 0,
          inPlay: false,
          drainFrames: 0,
          stuckFrames: 0,
        },
        plunger: {
          y: PLUNGER_REST_Y,
          pulling: false,
          power: 0,
        },
        flippers: {
          left: {
            angle: FLIPPER_REST_ANGLE,
            target: FLIPPER_REST_ANGLE,
            prevAngle: FLIPPER_REST_ANGLE,
          },
          right: {
            angle: -FLIPPER_REST_ANGLE,
            target: -FLIPPER_REST_ANGLE,
            prevAngle: -FLIPPER_REST_ANGLE,
          },
        },
        score: 0,
        hi: savedHi,
        balls: 3,
        status: 'ready',
        time: 0,
        floaters: [],
        particles: [],
        shake: 0,
        multiplier: 1,
        multiplierFrames: 0,
        manifestClears: 0,
        plungerLaneClosed: false,
      }
    }

    stateRef.current = initState()

    const startGame = (): void => {
      const s = initState()
      s.status = 'playing'
      stateRef.current = s
      setHud({
        score: 0,
        hi: s.hi,
        balls: 3,
        status: 'playing',
        muted: audio.muted,
        gatesLeft: 6,
      })
    }
    startGameRef.current = startGame

    const newBall = (): void => {
      const s = stateRef.current
      if (!s) return
      s.ball.x = PLUNGER_X
      s.ball.y = PLUNGER_REST_Y - BALL_RADIUS - 1
      s.ball.vx = 0
      s.ball.vy = 0
      s.ball.inPlay = false
      s.ball.drainFrames = 0
      s.ball.stuckFrames = 0
      s.multiplier = 1
      s.multiplierFrames = 0
      s.plungerLaneClosed = false
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      audio.init()
      keysRef.current[e.key] = true
      keysRef.current[e.key.toLowerCase()] = true
      if (['ArrowLeft', 'ArrowRight', 'ArrowDown', ' '].includes(e.key)) e.preventDefault()
      if (e.key === 'Escape' && onClose) onClose()
      const s = stateRef.current
      if (!s) return
      if (s.status === 'ready' && (e.key === ' ' || /^Arrow/.test(e.key))) {
        startGame()
        return
      }
      if (s.status === 'playing') {
        if ((e.key === 'ArrowLeft' || e.key.toLowerCase() === 'a') && !e.repeat) {
          s.flippers.left.target = FLIPPER_ACTIVE_ANGLE
          audio.flipper()
        }
        if ((e.key === 'ArrowRight' || e.key.toLowerCase() === 'd') && !e.repeat) {
          s.flippers.right.target = -FLIPPER_ACTIVE_ANGLE
          audio.flipper()
        }
        if ((e.key === 'ArrowDown' || e.key.toLowerCase() === 's') && !s.ball.inPlay) {
          s.plunger.pulling = true
        }
        if (e.key === ' ' && !s.ball.inPlay && s.plunger.pulling) {
          const power = s.plunger.power
          if (power > 5) {
            s.ball.vy = -power * PLUNGER_LAUNCH_POWER
            s.ball.inPlay = true
            audio.launch()
          }
          s.plunger.pulling = false
          s.plunger.power = 0
        }
        if (e.key === ' ' && !s.ball.inPlay && !s.plunger.pulling) {
          s.ball.vy = -10
          s.ball.inPlay = true
          audio.launch()
        }
        if (e.key === 'n' || e.key === 'N') {
          if (!s.ball.inPlay && s.balls > 0) newBall()
        }
      }
      if (e.key === 'm' || e.key === 'M') {
        audio.toggleMute()
        setHud(p => ({ ...p, muted: audio.muted }))
      }
      if (e.key === 'r' || e.key === 'R') {
        const cur = s.status
        if (cur === 'gameover' || cur === 'ready') startGame()
        else if (cur === 'playing') {
          if (window.confirm('Restart current shift? Your progress will be lost.')) startGame()
        }
      }
    }

    const onKeyUp = (e: KeyboardEvent): void => {
      keysRef.current[e.key] = false
      keysRef.current[e.key.toLowerCase()] = false
      const s = stateRef.current
      if (!s || s.status !== 'playing') return
      if (e.key === 'ArrowLeft' || e.key.toLowerCase() === 'a') {
        s.flippers.left.target = FLIPPER_REST_ANGLE
      }
      if (e.key === 'ArrowRight' || e.key.toLowerCase() === 'd') {
        s.flippers.right.target = -FLIPPER_REST_ANGLE
      }
      if (e.key === 'ArrowDown' || e.key.toLowerCase() === 's') {
        if (!s.ball.inPlay && s.plunger.pulling && s.plunger.power > 5) {
          s.ball.vy = -s.plunger.power * PLUNGER_LAUNCH_POWER
          s.ball.inPlay = true
          audio.launch()
        }
        s.plunger.pulling = false
        s.plunger.power = 0
      }
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    const explode = (x: number, y: number, color: string, count = 8): void => {
      const s = stateRef.current
      if (!s) return
      for (let i = 0; i < count; i++) {
        const a = rand(0, Math.PI * 2)
        const speed = rand(1, 4)
        s.particles.push({
          x,
          y,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          life: rand(20, 35),
          color,
          size: rand(1.2, 2.5),
        })
      }
    }

    const showFloater = (x: number, y: number, text: string, color: string): void => {
      const s = stateRef.current
      if (!s) return
      s.floaters.push({ x, y, text, color, life: 60 })
    }

    const addScore = (
      base: number,
      x: number,
      y: number,
      label: string | null = null,
      color = '#ffd166',
    ): void => {
      const s = stateRef.current
      if (!s) return
      const gain = base * s.multiplier
      s.score += gain
      const txt = s.multiplier > 1 ? `+${gain} (×${s.multiplier})` : `+${gain}`
      showFloater(x, y, label ? `${txt} ${label}` : txt, color)
    }

    interface FlipperCapsule {
      pivX: number
      pivY: number
      tipX: number
      tipY: number
      angle: number
    }

    const flipperCapsule = (side: 'left' | 'right'): FlipperCapsule | null => {
      const s = stateRef.current
      if (!s) return null
      const pivX = side === 'left' ? LEFT_FLIPPER_PIVOT_X : RIGHT_FLIPPER_PIVOT_X
      const pivY = FLIPPER_PIVOT_Y
      const angle = s.flippers[side].angle
      const dirX = side === 'left' ? 1 : -1
      const tipX = pivX + dirX * Math.cos(angle) * FLIPPER_LEN
      const tipY = pivY + Math.sin(angle) * FLIPPER_LEN
      return { pivX, pivY, tipX, tipY, angle }
    }

    const ballVsFlipper = (side: 'left' | 'right'): boolean => {
      const s = stateRef.current
      if (!s) return false
      const cap = flipperCapsule(side)
      if (!cap) return false
      const closest = closestPointOnSegment(
        s.ball.x,
        s.ball.y,
        cap.pivX,
        cap.pivY,
        cap.tipX,
        cap.tipY,
      )
      const dx = s.ball.x - closest.x
      const dy = s.ball.y - closest.y
      const distSq = dx * dx + dy * dy
      const minDist = BALL_RADIUS + FLIPPER_W / 2
      if (distSq < minDist * minDist && distSq > 0.0001) {
        const dist = Math.sqrt(distSq)
        const nx = dx / dist
        const ny = dy / dist
        const overlap = minDist - dist
        s.ball.x += nx * overlap
        s.ball.y += ny * overlap
        const angularVel = s.flippers[side].angle - s.flippers[side].prevAngle
        const rx = closest.x - cap.pivX
        const ry = closest.y - cap.pivY
        const dirX = side === 'left' ? 1 : -1
        const flipperVx = -ry * angularVel * dirX
        const flipperVy = rx * angularVel * dirX
        const vDotN = s.ball.vx * nx + s.ball.vy * ny
        if (vDotN < 0) {
          s.ball.vx -= 2 * vDotN * nx
          s.ball.vy -= 2 * vDotN * ny
          s.ball.vx *= 0.85
          s.ball.vy *= 0.85
        }
        const couplingFactor = 1.4
        s.ball.vx += flipperVx * couplingFactor
        s.ball.vy += flipperVy * couplingFactor
        if (angularVel < -0.05) {
          const kickMag = 4 * (closest.t || 0)
          s.ball.vx += dirX * Math.cos(cap.angle) * kickMag
          s.ball.vy += Math.sin(cap.angle) * kickMag
        }
        return true
      }
      return false
    }

    const ballVsSegment = (seg: Segment, restitution = RESTITUTION_WALL): number => {
      const s = stateRef.current
      if (!s) return 0
      const closest = closestPointOnSegment(s.ball.x, s.ball.y, seg.x1, seg.y1, seg.x2, seg.y2)
      const dx = s.ball.x - closest.x
      const dy = s.ball.y - closest.y
      const distSq = dx * dx + dy * dy
      if (distSq < BALL_RADIUS * BALL_RADIUS && distSq > 0.0001) {
        const dist = Math.sqrt(distSq)
        const nx = dx / dist
        const ny = dy / dist
        const overlap = BALL_RADIUS - dist
        s.ball.x += nx * overlap
        s.ball.y += ny * overlap
        const vDotN = s.ball.vx * nx + s.ball.vy * ny
        let impactSpeed = 0
        if (vDotN < 0) {
          impactSpeed = -vDotN
          s.ball.vx -= (1 + restitution) * vDotN * nx
          s.ball.vy -= (1 + restitution) * vDotN * ny
        }
        return impactSpeed
      }
      return 0
    }

    const ballVsBumper = (bumper: Bumper): boolean => {
      const s = stateRef.current
      if (!s) return false
      const dx = s.ball.x - bumper.x
      const dy = s.ball.y - bumper.y
      const distSq = dx * dx + dy * dy
      const minDist = BALL_RADIUS + bumper.r
      if (distSq < minDist * minDist && distSq > 0.0001) {
        const dist = Math.sqrt(distSq)
        const nx = dx / dist
        const ny = dy / dist
        const overlap = minDist - dist
        s.ball.x += nx * overlap
        s.ball.y += ny * overlap
        const vDotN = s.ball.vx * nx + s.ball.vy * ny
        if (vDotN < 0) {
          s.ball.vx -= (1 + RESTITUTION_BUMPER) * vDotN * nx
          s.ball.vy -= (1 + RESTITUTION_BUMPER) * vDotN * ny
        }
        const kick = 3.5
        s.ball.vx += nx * kick
        s.ball.vy += ny * kick
        bumper.hits++
        bumper.flashFrames = 12
        if (bumper.hits >= BUMPER_SUPER_THRESHOLD && !bumper.super) {
          bumper.super = true
          showFloater(bumper.x, bumper.y - 30, 'SUPER BUMPER!', '#d94a8f')
        }
        const score = bumper.super ? SCORE_BUMPER_SUPER : SCORE_BUMPER
        addScore(score, bumper.x, bumper.y - 14, null, bumper.super ? '#d94a8f' : '#ffd166')
        audio.bumper(bumper.super)
        explode(bumper.x, bumper.y, bumper.super ? '#d94a8f' : '#ffd166', 8)
        return true
      }
      return false
    }

    const ballVsSlingshot = (sling: Slingshot): boolean => {
      const s = stateRef.current
      if (!s) return false
      const closest = closestPointOnSegment(
        s.ball.x,
        s.ball.y,
        sling.x1,
        sling.y1,
        sling.x2,
        sling.y2,
      )
      const dx = s.ball.x - closest.x
      const dy = s.ball.y - closest.y
      const distSq = dx * dx + dy * dy
      if (distSq < BALL_RADIUS * BALL_RADIUS && distSq > 0.0001) {
        const dist = Math.sqrt(distSq)
        const nx = dx / dist
        const ny = dy / dist
        const overlap = BALL_RADIUS - dist
        s.ball.x += nx * overlap
        s.ball.y += ny * overlap
        const vDotN = s.ball.vx * nx + s.ball.vy * ny
        if (vDotN < 0) {
          s.ball.vx -= (1 + 0.95) * vDotN * nx
          s.ball.vy -= (1 + 0.95) * vDotN * ny
        }
        s.ball.vx += nx * 3.5
        s.ball.vy += ny * 3.5
        sling.flashFrames = 10
        addScore(SCORE_SLINGSHOT, closest.x, closest.y - 12)
        audio.bumper(false)
        return true
      }
      return false
    }

    const ballVsGate = (gate: Gate): boolean => {
      if (gate.down) return false
      const s = stateRef.current
      if (!s) return false
      const halfW = gate.w / 2
      const halfH = gate.h / 2
      const cx = clamp(s.ball.x, gate.x - halfW, gate.x + halfW)
      const cy = clamp(s.ball.y, gate.y - halfH, gate.y + halfH)
      const dx = s.ball.x - cx
      const dy = s.ball.y - cy
      const distSq = dx * dx + dy * dy
      if (distSq < BALL_RADIUS * BALL_RADIUS) {
        if (Math.abs(dx) > Math.abs(dy)) {
          s.ball.vx = -s.ball.vx * 0.6
          s.ball.x = dx > 0 ? gate.x + halfW + BALL_RADIUS : gate.x - halfW - BALL_RADIUS
        } else {
          s.ball.vy = -s.ball.vy * 0.6
          s.ball.y = dy > 0 ? gate.y + halfH + BALL_RADIUS : gate.y - halfH - BALL_RADIUS
        }
        gate.down = true
        gate.flashFrames = 18
        addScore(SCORE_GATE, gate.x, gate.y - 12)
        audio.gate()
        explode(gate.x, gate.y, '#3a8fd6', 12)
        const allDown = s.gates.every(g => g.down)
        if (allDown) {
          s.score += SCORE_GATE_ALL * s.multiplier
          showFloater(W / 2, H / 2, `+${SCORE_GATE_ALL * s.multiplier} MANIFEST CLEARED`, '#ffd166')
          audio.manifestCleared()
          s.shake = 12
          s.manifestClears++
          setTimeout(() => {
            const cur = stateRef.current
            if (!cur) return
            for (const g of cur.gates) g.down = false
          }, 1500)
        }
        return true
      }
      return false
    }

    const ballVsLane = (lane: Lane): boolean => {
      if (lane.lit) return false
      const s = stateRef.current
      if (!s) return false
      const dx = s.ball.x - lane.x
      const dy = s.ball.y - lane.y
      if (dx * dx + dy * dy < 14 * 14) {
        lane.lit = true
        addScore(SCORE_LANE, lane.x, lane.y + 18, 'LANE')
        const allLit = s.lanes.every(l => l.lit)
        if (allLit) {
          s.score += SCORE_LANE_ALL * s.multiplier
          showFloater(
            W / 2,
            H / 4,
            `+${SCORE_LANE_ALL * s.multiplier} BERTH ASSIGNED · ×2`,
            '#3a9bd6',
          )
          s.multiplier = 2
          s.multiplierFrames = 60 * 30
          setTimeout(() => {
            const cur = stateRef.current
            if (!cur) return
            for (const l of cur.lanes) l.lit = false
          }, 1500)
        }
        return true
      }
      return false
    }

    const ballVsSpinner = (sp: Spinner): boolean => {
      const s = stateRef.current
      if (!s) return false
      const halfL = 12
      const closest = closestPointOnSegment(
        s.ball.x,
        s.ball.y,
        sp.x - halfL,
        sp.y,
        sp.x + halfL,
        sp.y,
      )
      const dx = s.ball.x - closest.x
      const dy = s.ball.y - closest.y
      const distSq = dx * dx + dy * dy
      if (distSq < (BALL_RADIUS + 2) * (BALL_RADIUS + 2)) {
        sp.angularVel = clamp(s.ball.vx * 0.15, -0.5, 0.5)
        addScore(SCORE_SPINNER, closest.x, closest.y - 10)
        audio.spinner()
        return true
      }
      return false
    }

    const physicsStep = (dtScale: number): void => {
      const s = stateRef.current
      if (!s) return
      s.ball.vy += GRAVITY * dtScale
      s.ball.vx *= FRICTION
      s.ball.vy *= FRICTION
      const maxV = 24
      const sp = Math.hypot(s.ball.vx, s.ball.vy)
      if (sp > maxV) {
        s.ball.vx = (s.ball.vx / sp) * maxV
        s.ball.vy = (s.ball.vy / sp) * maxV
      }
      s.ball.x += s.ball.vx * dtScale
      s.ball.y += s.ball.vy * dtScale

      const PLANE_INNER_X = W - 28 - 8
      if (s.ball.x > PLANE_INNER_X) {
        const plungerTopY = s.plunger.y - BALL_RADIUS
        if (s.ball.y > plungerTopY) {
          s.ball.y = plungerTopY
          if (s.ball.vy > 0) s.ball.vy = -s.ball.vy * 0.4
          s.ball.vx *= 0.7
          if (Math.abs(s.ball.vy) < 0.5 && Math.abs(s.ball.vx) < 0.5) {
            s.ball.inPlay = false
            s.ball.vx = 0
            s.ball.vy = 0
            s.ball.x = PLUNGER_X
            s.ball.y = s.plunger.y - BALL_RADIUS - 1
          }
        }
      }

      for (const seg of s.segments) ballVsSegment(seg)
      if (s.plungerLaneClosed) ballVsSegment(s.laneClosure)
      for (const b of s.bumpers) ballVsBumper(b)
      for (const sl of s.slingshots) ballVsSlingshot(sl)
      for (const g of s.gates) ballVsGate(g)
      for (const l of s.lanes) ballVsLane(l)
      for (const spin of s.spinners) ballVsSpinner(spin)
      ballVsFlipper('left')
      ballVsFlipper('right')

      // One-way gate: once the ball is firmly in the playfield, close the
      // lane behind it so it can't drift back into the plunger corridor.
      if (
        s.ball.inPlay &&
        !s.plungerLaneClosed &&
        s.ball.x < s.laneClosure.x1 - 40 &&
        s.ball.y > s.laneClosure.y2 + 20
      ) {
        s.plungerLaneClosed = true
      }
    }

    let animationId = 0

    const draw = (): void => {
      const s = stateRef.current
      if (!s) {
        animationId = requestAnimationFrame(draw)
        return
      }
      s.time++

      const screenW = canvas.width / dpr
      const screenH = canvas.height / dpr
      const scale = Math.min(screenW / W, screenH / H, 2.0)
      const offsetX = (screenW - W * scale) / 2
      const offsetY = (screenH - H * scale) / 2

      let shakeX = 0
      let shakeY = 0
      if (s.shake > 0) {
        shakeX = rand(-s.shake, s.shake)
        shakeY = rand(-s.shake, s.shake)
        s.shake *= 0.85
        if (s.shake < 0.3) s.shake = 0
      }
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, shakeY * dpr)

      ctx.fillStyle = '#080e18'
      ctx.fillRect(0, 0, screenW, screenH)

      ctx.save()
      ctx.translate(offsetX, offsetY)
      ctx.scale(scale, scale)

      const pfGrad = ctx.createLinearGradient(0, 0, 0, H)
      pfGrad.addColorStop(0, '#1a2436')
      pfGrad.addColorStop(0.5, '#152030')
      pfGrad.addColorStop(1, '#0d1622')
      ctx.fillStyle = pfGrad
      ctx.fillRect(0, 0, W, H)

      ctx.strokeStyle = 'rgba(60, 80, 110, 0.12)'
      ctx.lineWidth = 0.5
      for (let i = 1; i < 12; i++) {
        ctx.beginPath()
        ctx.moveTo(0, i * (H / 12))
        ctx.lineTo(W, i * (H / 12))
        ctx.stroke()
      }
      for (let i = 1; i < 8; i++) {
        ctx.beginPath()
        ctx.moveTo(i * (W / 8), 0)
        ctx.lineTo(i * (W / 8), H)
        ctx.stroke()
      }

      if (s.status === 'playing') {
        const sides: Array<'left' | 'right'> = ['left', 'right']
        for (const side of sides) {
          const f = s.flippers[side]
          f.prevAngle = f.angle
          const sgn = side === 'left' ? 1 : -1
          const target = f.target
          const diff = target - f.angle
          const speed =
            Math.abs(target) > Math.abs(f.angle) || sgn * target < sgn * f.angle
              ? FLIPPER_SWING_SPEED
              : FLIPPER_RETURN_SPEED
          if (Math.abs(diff) < speed) f.angle = target
          else f.angle += Math.sign(diff) * speed
        }

        if (s.plunger.pulling) {
          s.plunger.power = Math.min(s.plunger.power + 1.5, PLUNGER_MAX_PULL)
          s.plunger.y = PLUNGER_REST_Y + s.plunger.power
          if (!s.ball.inPlay) {
            s.ball.y = s.plunger.y - BALL_RADIUS - 1
          }
        } else if (!s.ball.inPlay) {
          s.plunger.y += (PLUNGER_REST_Y - s.plunger.y) * 0.3
        }

        if (s.multiplierFrames > 0) {
          s.multiplierFrames--
          if (s.multiplierFrames === 0) s.multiplier = 1
        }

        if (s.ball.inPlay) {
          for (let i = 0; i < SUB_STEPS; i++) {
            physicsStep(1 / SUB_STEPS)
          }
        }

        const dropBall = (stuckMessage: string | null): void => {
          s.ball.inPlay = false
          s.ball.drainFrames = 60
          s.ball.stuckFrames = 0
          audio.drain()
          s.balls--
          for (const b of s.bumpers) {
            b.hits = 0
            b.super = false
          }
          for (const l of s.lanes) l.lit = false
          if (stuckMessage) {
            s.floaters.push({
              x: s.ball.x,
              y: s.ball.y - 14,
              text: stuckMessage,
              color: '#d97a2c',
              life: 90,
            })
          }
        }

        if (s.ball.inPlay && s.ball.y > H + 30) {
          dropBall(null)
        }

        if (s.ball.inPlay) {
          const speed2 = s.ball.vx * s.ball.vx + s.ball.vy * s.ball.vy
          if (speed2 < STUCK_SPEED * STUCK_SPEED) {
            s.ball.stuckFrames++
            if (s.ball.stuckFrames >= STUCK_THRESHOLD_FRAMES) {
              dropBall('BALL STUCK · LOST')
            }
          } else {
            s.ball.stuckFrames = 0
          }
        } else {
          s.ball.stuckFrames = 0
        }

        if (!s.ball.inPlay && s.ball.drainFrames > 0) {
          s.ball.drainFrames--
          if (s.ball.drainFrames === 0) {
            if (s.balls > 0) {
              newBall()
            } else {
              s.status = 'gameover'
              if (s.score > s.hi) {
                s.hi = s.score
                try {
                  localStorage.setItem('containerPinballHi', String(s.hi))
                } catch {
                  // localStorage may be unavailable; ignore.
                }
              }
              setHud(p => ({ ...p, status: 'gameover', hi: s.hi }))
            }
          }
        }

        for (const sp of s.spinners) {
          sp.angle += sp.angularVel
          sp.angularVel *= 0.96
          if (Math.abs(sp.angularVel) < 0.01) sp.angularVel = 0
        }

        for (const b of s.bumpers) if (b.flashFrames > 0) b.flashFrames--
        for (const sl of s.slingshots) if (sl.flashFrames > 0) sl.flashFrames--
        for (const g of s.gates) if (g.flashFrames > 0) g.flashFrames--

        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.15
          p.vx *= 0.96
          p.life--
          return p.life > 0
        })
        s.floaters = s.floaters.filter(f => {
          f.y -= 0.5
          f.life--
          return f.life > 0
        })

        setHud(prev => {
          const gatesLeft = s.gates.filter(g => !g.down).length
          if (
            prev.score === s.score &&
            prev.balls === s.balls &&
            prev.gatesLeft === gatesLeft &&
            prev.status === s.status
          )
            return prev
          return { ...prev, score: s.score, balls: s.balls, gatesLeft, status: s.status }
        })
      }

      ctx.strokeStyle = '#ffd166'
      ctx.lineWidth = 2
      for (const seg of s.segments) {
        ctx.beginPath()
        ctx.moveTo(seg.x1, seg.y1)
        ctx.lineTo(seg.x2, seg.y2)
        ctx.stroke()
      }
      ctx.strokeStyle = 'rgba(255, 209, 102, 0.15)'
      ctx.lineWidth = 5
      for (const seg of s.segments) {
        ctx.beginPath()
        ctx.moveTo(seg.x1, seg.y1)
        ctx.lineTo(seg.x2, seg.y2)
        ctx.stroke()
      }

      // Lane-closure gate: faint ghost while open so the player can see where
      // it sits, solid + glowing once the ball has passed and it's locked.
      const lc = s.laneClosure
      if (s.plungerLaneClosed) {
        ctx.strokeStyle = 'rgba(217, 74, 143, 0.3)'
        ctx.lineWidth = 6
        ctx.beginPath()
        ctx.moveTo(lc.x1, lc.y1)
        ctx.lineTo(lc.x2, lc.y2)
        ctx.stroke()
        ctx.strokeStyle = '#d94a8f'
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(lc.x1, lc.y1)
        ctx.lineTo(lc.x2, lc.y2)
        ctx.stroke()
      } else {
        ctx.strokeStyle = 'rgba(217, 74, 143, 0.18)'
        ctx.lineWidth = 1
        ctx.setLineDash([3, 4])
        ctx.beginPath()
        ctx.moveTo(lc.x1, lc.y1)
        ctx.lineTo(lc.x2, lc.y2)
        ctx.stroke()
        ctx.setLineDash([])
      }

      for (const lane of s.lanes) drawLane(ctx, lane, s.time)
      for (const sp of s.spinners) drawSpinner(ctx, sp)
      for (const g of s.gates) drawGate(ctx, g)
      for (const sl of s.slingshots) drawSlingshot(ctx, sl)
      for (const b of s.bumpers) drawBumper(ctx, b)
      drawPlunger(ctx, s.plunger)
      drawFlipper(ctx, 'left', s.flippers.left)
      drawFlipper(ctx, 'right', s.flippers.right)

      if (s.ball.inPlay || (s.balls > 0 && s.ball.drainFrames === 0)) {
        drawBall(ctx, s.ball)
      }

      s.particles.forEach(p => {
        const alpha = Math.min(1, p.life / 25)
        ctx.fillStyle = p.color
        ctx.globalAlpha = alpha
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size)
      })
      ctx.globalAlpha = 1

      s.floaters.forEach(f => {
        const alpha = Math.min(1, f.life / 30)
        ctx.fillStyle = f.color
        ctx.globalAlpha = alpha
        ctx.font = 'bold 11px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(f.text, f.x, f.y)
      })
      ctx.globalAlpha = 1

      ctx.fillStyle = 'rgba(90, 100, 120, 0.6)'
      ctx.font = '8px ui-monospace, Menlo, monospace'
      ctx.textAlign = 'center'
      ctx.save()
      ctx.translate(W - 22, H / 2)
      ctx.rotate(-Math.PI / 2)
      ctx.fillText('PLUNGER LANE — HOLD ↓ THEN SPACE', 0, 4)
      ctx.restore()

      ctx.restore()

      const vignette = ctx.createRadialGradient(
        screenW / 2,
        screenH / 2,
        screenH * 0.4,
        screenW / 2,
        screenH / 2,
        screenH,
      )
      vignette.addColorStop(0, 'rgba(0,0,0,0)')
      vignette.addColorStop(1, 'rgba(0,0,0,0.5)')
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, screenW, screenH)

      animationId = requestAnimationFrame(draw)
    }
    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('resize', resize)
    }
  }, [onClose])

  return (
    <div style={styles.root}>
      <div style={styles.hud}>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SCORE</span>
          <span style={styles.hudValue}>{String(hud.score).padStart(7, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BEST</span>
          <span style={styles.hudValue}>{String(hud.hi).padStart(7, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BALLS</span>
          <span style={styles.hudValue}>{'◉ '.repeat(Math.max(0, hud.balls))}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>GATES</span>
          <span style={styles.hudValue}>{6 - hud.gatesLeft} / 6</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>AUDIO</span>
          <span style={styles.hudValue}>{hud.muted ? '○ MUTE' : '● ON'}</span>
        </div>
        <button
          style={styles.restart}
          onClick={() => {
            try {
              audioRef.current.init()
            } catch {
              // ignore audio init failures
            }
            if (startGameRef.current) startGameRef.current()
          }}
          onMouseDown={e => e.preventDefault()}
          title='Restart shift'
        >
          ↻ RESTART
        </button>
        {onClose && (
          <button style={styles.close} onClick={onClose}>
            ESC
          </button>
        )}
      </div>

      <canvas ref={canvasRef} style={styles.canvas} />

      {hud.status === 'ready' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>CONTAINER PINBALL</div>
          <div style={styles.overlaySubLarge}>
            keep the container in play · hit the gates · score the shift
          </div>
          <div style={styles.legend}>
            <div style={styles.legendRow}>← / A : left reach stacker (flipper)</div>
            <div style={styles.legendRow}>→ / D : right reach stacker (flipper)</div>
            <div style={styles.legendRow}>↓ / S : pull plunger · release or SPACE to launch</div>
            <div style={styles.legendRow}>N : new ball after drain · R restart · M mute</div>
          </div>
          <div style={styles.controls}>
            <span style={{ color: '#5a6478' }}>
              hit all 6 inspection gates → MANIFEST CLEARED bonus
              <br />
              roll through all 3 top lanes → BERTH ASSIGNED · ×2 multiplier
              <br />
              pop the same bumper 5 times → SUPER BUMPER
            </span>
          </div>
          <div style={{ ...styles.controls, marginTop: 16 }}>press SPACE to start</div>
        </div>
      )}

      {hud.status === 'gameover' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>SHIFT COMPLETE</div>
          <div style={styles.overlaySub}>
            final score: {hud.score.toLocaleString()}{' '}
            {hud.score >= hud.hi && hud.score > 0 ? '· NEW BEST' : ''}
          </div>
          <pre style={styles.report}>
            {`SHIFT REPORT — ${COMPANY_NAME}
${hud.score.toLocaleString()} TEU credited this shift
${hud.score >= hud.hi && hud.score > 0 ? 'operator commendation issued' : 'see you next shift'}`}
          </pre>
          <div style={styles.overlaySub}>press R or click RESTART to try again · ESC to close</div>
        </div>
      )}
    </div>
  )
}

export default ContainerPinball

// ============= DRAWING =============

function drawBall(ctx: CanvasRenderingContext2D, ball: Ball): void {
  const x = ball.x
  const y = ball.y
  ctx.fillStyle = 'rgba(0,0,0,0.4)'
  ctx.fillRect(x - BALL_RADIUS + 1, y - BALL_RADIUS + 2, BALL_RADIUS * 2, BALL_RADIUS * 2)
  ctx.fillStyle = '#3a8fd6'
  ctx.fillRect(x - BALL_RADIUS, y - BALL_RADIUS, BALL_RADIUS * 2, BALL_RADIUS * 2)
  ctx.fillStyle = 'rgba(255,255,255,0.3)'
  ctx.fillRect(x - BALL_RADIUS, y - BALL_RADIUS, BALL_RADIUS * 2, 1.5)
  ctx.fillStyle = 'rgba(0,0,0,0.4)'
  ctx.fillRect(x - BALL_RADIUS, y + BALL_RADIUS - 1.5, BALL_RADIUS * 2, 1.5)
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.5
  for (let i = 1; i < 4; i++) {
    const lx = x - BALL_RADIUS + ((BALL_RADIUS * 2) / 4) * i
    ctx.beginPath()
    ctx.moveTo(lx, y - BALL_RADIUS + 1.5)
    ctx.lineTo(lx, y + BALL_RADIUS - 1.5)
    ctx.stroke()
  }
  ctx.strokeStyle = 'rgba(0,0,0,0.6)'
  ctx.lineWidth = 0.8
  ctx.strokeRect(x - BALL_RADIUS, y - BALL_RADIUS, BALL_RADIUS * 2, BALL_RADIUS * 2)
}

function drawBumper(ctx: CanvasRenderingContext2D, b: Bumper): void {
  const flash = b.flashFrames / 12
  ctx.fillStyle = b.super
    ? `rgba(217, 74, 143, ${0.25 + flash * 0.4})`
    : `rgba(255, 209, 102, ${0.15 + flash * 0.3})`
  ctx.beginPath()
  ctx.arc(b.x, b.y, b.r + 8, 0, Math.PI * 2)
  ctx.fill()
  const baseColor = b.super ? '#d94a8f' : '#3a8fd6'
  const altColor = b.super ? '#c43872' : '#2d6da3'
  ctx.fillStyle = '#0d1218'
  ctx.beginPath()
  ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2)
  ctx.fill()
  const slices = 8
  for (let i = 0; i < slices; i++) {
    const a1 = (i / slices) * Math.PI * 2
    const a2 = ((i + 1) / slices) * Math.PI * 2
    ctx.fillStyle = i % 2 === 0 ? baseColor : altColor
    ctx.beginPath()
    ctx.moveTo(b.x, b.y)
    ctx.arc(b.x, b.y, b.r - 2, a1, a2)
    ctx.closePath()
    ctx.fill()
  }
  ctx.fillStyle = b.super ? '#ffd166' : '#1a2236'
  ctx.beginPath()
  ctx.arc(b.x, b.y, b.r * 0.45, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = b.super ? '#ffd166' : 'rgba(255, 209, 102, 0.5)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2)
  ctx.stroke()
  ctx.fillStyle = b.super ? '#1a1a1a' : '#ffd166'
  ctx.font = 'bold 11px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(b.super ? '★' : `${b.hits}`, b.x, b.y)
  ctx.textBaseline = 'alphabetic'
  if (flash > 0) {
    ctx.fillStyle = `rgba(255, 255, 255, ${flash * 0.5})`
    ctx.beginPath()
    ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2)
    ctx.fill()
  }
}

function drawSlingshot(ctx: CanvasRenderingContext2D, sl: Slingshot): void {
  const dx = sl.x2 - sl.x1
  const dy = sl.y2 - sl.y1
  const len = Math.hypot(dx, dy)
  const nx = -dy / len
  const ny = dx / len
  ctx.fillStyle = 'rgba(217, 122, 44, 0.25)'
  ctx.beginPath()
  ctx.moveTo(sl.x1 - nx * 18, sl.y1 - ny * 18)
  ctx.lineTo(sl.x2 - nx * 18, sl.y2 - ny * 18)
  ctx.lineTo(sl.x2, sl.y2)
  ctx.lineTo(sl.x1, sl.y1)
  ctx.closePath()
  ctx.fill()
  const flash = sl.flashFrames / 10
  ctx.strokeStyle = flash > 0 ? `rgba(255, 255, 255, ${flash})` : '#ffd166'
  ctx.lineWidth = flash > 0 ? 4 : 3
  ctx.beginPath()
  ctx.moveTo(sl.x1, sl.y1)
  ctx.lineTo(sl.x2, sl.y2)
  ctx.stroke()
  const steps = Math.floor(len / 8)
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)'
  ctx.lineWidth = 1.5
  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) / steps
    const cx = sl.x1 + dx * t
    const cy = sl.y1 + dy * t
    ctx.beginPath()
    ctx.moveTo(cx - (dx / len) * 2 + nx * 1, cy - (dy / len) * 2 + ny * 1)
    ctx.lineTo(cx + (dx / len) * 2 - nx * 1, cy + (dy / len) * 2 - ny * 1)
    ctx.stroke()
  }
}

function drawGate(ctx: CanvasRenderingContext2D, g: Gate): void {
  if (g.down) {
    ctx.fillStyle = 'rgba(58, 143, 214, 0.1)'
    ctx.fillRect(g.x - g.w / 2, g.y - g.h / 2, g.w, g.h)
    ctx.strokeStyle = 'rgba(58, 143, 214, 0.2)'
    ctx.lineWidth = 0.8
    ctx.setLineDash([2, 3])
    ctx.strokeRect(g.x - g.w / 2, g.y - g.h / 2, g.w, g.h)
    ctx.setLineDash([])
    return
  }
  const flash = g.flashFrames / 18
  ctx.fillStyle = '#3a8fd6'
  ctx.fillRect(g.x - g.w / 2, g.y - g.h / 2, g.w, g.h)
  ctx.fillStyle = 'rgba(255,255,255,0.2)'
  ctx.fillRect(g.x - g.w / 2, g.y - g.h / 2, g.w, 2)
  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.fillRect(g.x - g.w / 2, g.y + g.h / 2 - 2, g.w, 2)
  ctx.strokeStyle = '#ffd166'
  ctx.lineWidth = 1.5
  ctx.strokeRect(g.x - g.w / 2, g.y - g.h / 2, g.w, g.h)
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.5
  for (let i = 1; i < 4; i++) {
    const lx = g.x - g.w / 2 + (g.w / 4) * i
    ctx.beginPath()
    ctx.moveTo(lx, g.y - g.h / 2 + 2)
    ctx.lineTo(lx, g.y + g.h / 2 - 2)
    ctx.stroke()
  }
  ctx.fillStyle = 'rgba(255,255,255,0.85)'
  ctx.font = 'bold 8px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('GATE', g.x, g.y)
  ctx.textBaseline = 'alphabetic'
  if (flash > 0) {
    ctx.fillStyle = `rgba(255, 255, 255, ${flash * 0.6})`
    ctx.fillRect(g.x - g.w / 2, g.y - g.h / 2, g.w, g.h)
  }
}

function drawLane(ctx: CanvasRenderingContext2D, lane: Lane, time: number): void {
  const r = 14
  ctx.strokeStyle = lane.lit ? '#3a9bd6' : 'rgba(120, 130, 150, 0.4)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(lane.x, lane.y, r, 0, Math.PI * 2)
  ctx.stroke()
  if (lane.lit) {
    const pulse = 0.4 + Math.sin(time * 0.2) * 0.2
    ctx.fillStyle = `rgba(58, 155, 214, ${pulse})`
    ctx.beginPath()
    ctx.arc(lane.x, lane.y, r - 4, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.fillStyle = lane.lit ? '#0d1218' : '#5a6478'
  ctx.font = 'bold 9px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('L', lane.x, lane.y + 1)
  ctx.textBaseline = 'alphabetic'
}

function drawSpinner(ctx: CanvasRenderingContext2D, sp: Spinner): void {
  ctx.save()
  ctx.translate(sp.x, sp.y)
  ctx.rotate(sp.angle)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(-12, -1, 24, 2)
  ctx.fillStyle = '#5a4520'
  ctx.fillRect(-13, -2, 2, 4)
  ctx.fillRect(11, -2, 2, 4)
  ctx.restore()
  ctx.fillStyle = '#3a3a3a'
  ctx.beginPath()
  ctx.arc(sp.x, sp.y, 2.5, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = 'rgba(90, 100, 120, 0.6)'
  ctx.font = '8px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.fillText('TURNSTILE', sp.x, sp.y + 18)
}

function drawFlipper(ctx: CanvasRenderingContext2D, side: 'left' | 'right', f: Flipper): void {
  const pivX = side === 'left' ? LEFT_FLIPPER_PIVOT_X : RIGHT_FLIPPER_PIVOT_X
  const pivY = FLIPPER_PIVOT_Y
  const dirX = side === 'left' ? 1 : -1
  const tipX = pivX + dirX * Math.cos(f.angle) * FLIPPER_LEN
  const tipY = pivY + Math.sin(f.angle) * FLIPPER_LEN

  ctx.lineCap = 'round'
  ctx.strokeStyle = '#5a4520'
  ctx.lineWidth = FLIPPER_W + 2
  ctx.beginPath()
  ctx.moveTo(pivX, pivY)
  ctx.lineTo(tipX, tipY)
  ctx.stroke()
  ctx.strokeStyle = '#c9a648'
  ctx.lineWidth = FLIPPER_W
  ctx.beginPath()
  ctx.moveTo(pivX, pivY)
  ctx.lineTo(tipX, tipY)
  ctx.stroke()
  ctx.lineCap = 'butt'

  const cosA = Math.cos(f.angle)
  const sinA = Math.sin(f.angle)
  const px = -sinA
  const py = cosA
  ctx.strokeStyle = '#1a1a1a'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(pivX + dirX * px * 4, pivY + py * 4)
  ctx.lineTo(tipX + dirX * px * 4, tipY + py * 4)
  ctx.stroke()

  ctx.fillStyle = '#5a4520'
  ctx.beginPath()
  ctx.arc(pivX, pivY, FLIPPER_W / 2 + 2, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#c9a648'
  ctx.beginPath()
  ctx.arc(pivX, pivY, FLIPPER_W / 2 - 1, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#1a1a1a'
  ctx.beginPath()
  ctx.arc(pivX, pivY, 2, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = '#3a3a3a'
  ctx.beginPath()
  ctx.arc(tipX, tipY, FLIPPER_W / 2 + 1, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#c9a648'
  ctx.beginPath()
  ctx.arc(tipX, tipY, FLIPPER_W / 2 - 1, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = '#3a2c12'
  ctx.fillRect(pivX - 16, pivY + 4, 32, 14)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(pivX - 14, pivY + 6, 28, 10)
  ctx.fillStyle = '#1a2236'
  ctx.fillRect(pivX - (side === 'left' ? 4 : 12), pivY + 7, 16, 8)
  ctx.fillStyle = '#3a5a8a'
  ctx.fillRect(pivX - (side === 'left' ? 2 : 10), pivY + 8, 12, 5)
  ctx.fillStyle = '#0d0d0d'
  ctx.fillRect(pivX - 14, pivY + 16, 4, 3)
  ctx.fillRect(pivX + 10, pivY + 16, 4, 3)
}

function drawPlunger(ctx: CanvasRenderingContext2D, p: Plunger): void {
  const x = PLUNGER_X
  ctx.strokeStyle = '#5a5a5a'
  ctx.lineWidth = 4
  ctx.beginPath()
  ctx.moveTo(x, p.y)
  ctx.lineTo(x, H - 8)
  ctx.stroke()
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(x - 8, p.y - 6, 16, 12)
  ctx.fillStyle = 'rgba(0,0,0,0.4)'
  ctx.fillRect(x - 8, p.y + 4, 16, 2)
  ctx.strokeStyle = '#5a4520'
  ctx.lineWidth = 1.2
  ctx.strokeRect(x - 8, p.y - 6, 16, 12)
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(x - 1, p.y - 6, 2, 4)
  if (p.pulling) {
    ctx.strokeStyle = 'rgba(120, 130, 150, 0.5)'
    ctx.lineWidth = 1
    const coilCount = 6
    const stretchY = p.y - PLUNGER_REST_Y
    for (let i = 0; i < coilCount; i++) {
      const cy = PLUNGER_REST_Y - 60 + (i / coilCount) * (60 + stretchY)
      ctx.beginPath()
      ctx.moveTo(x - 5, cy)
      ctx.bezierCurveTo(x + 5, cy + 2, x + 5, cy + 4, x - 5, cy + 6)
      ctx.stroke()
    }
  }
  if (p.power > 0) {
    ctx.fillStyle = '#ffd166'
    ctx.font = 'bold 9px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.fillText(`${Math.round((p.power / PLUNGER_MAX_PULL) * 100)}%`, x, p.y - 16)
  }
}

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    background: '#080e18',
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    color: '#cfd8e8',
    zIndex: 9999,
    overflow: 'hidden',
  },
  canvas: { width: '100%', height: '100%', display: 'block', touchAction: 'none' },
  hud: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '14px 22px',
    display: 'flex',
    gap: 24,
    alignItems: 'center',
    fontSize: 11,
    letterSpacing: '0.18em',
    pointerEvents: 'none',
    zIndex: 2,
    background: 'linear-gradient(to bottom, rgba(8,14,24,0.85), rgba(8,14,24,0))',
  },
  hudGroup: { display: 'flex', flexDirection: 'column', gap: 2 },
  hudLabel: { color: '#5a6478', fontSize: 9 },
  hudValue: { color: '#ffd166', fontSize: 13, letterSpacing: '0.1em' },
  restart: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 4,
    background: 'transparent',
    border: '1px solid #4a3a18',
    color: '#ffd166',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  close: {
    marginLeft: 8,
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 4,
    background: 'transparent',
    border: '1px solid #2a3548',
    color: '#7a8398',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: 'rgba(8, 14, 24, 0.7)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  overlayTitle: {
    fontSize: 32,
    letterSpacing: '0.32em',
    color: '#ffd166',
  },
  overlaySub: { fontSize: 12, letterSpacing: '0.18em', color: '#7a8398' },
  overlaySubLarge: {
    fontSize: 13,
    letterSpacing: '0.22em',
    color: '#cfd8e8',
    marginBottom: 4,
    maxWidth: 380,
    textAlign: 'center',
  },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 11,
    letterSpacing: '0.14em',
    color: '#cfd8e8',
    marginTop: 8,
  },
  legendRow: { textAlign: 'center' },
  controls: {
    marginTop: 8,
    fontSize: 10,
    letterSpacing: '0.18em',
    color: '#7a8398',
    textAlign: 'center',
    lineHeight: 1.7,
  },
  report: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
    padding: '16px 22px',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#cfd8e8',
    margin: 0,
    maxWidth: 380,
  },
}
